import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Tractor,
    BadgeCheck,
    Slice,
    Scale,
    Hourglass,
    Package,
    Truck,
    Award,
    Layers,
    HeartHandshake,
    Activity,
    QrCode,
    Gem,
    Leaf,
    Star,
    ChevronLeft,
    ChevronRight,
    ArrowRight,
    Gift,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { UKMap } from "@/components/UKMap";
import { UnionJack } from "@/components/UnionJack";
import { getProducts, getFarms, getReviews } from "@/lib/api";

const HERO_IMAGE =
    "https://images.pexels.com/photos/31205726/pexels-photo-31205726.jpeg?auto=compress&cs=tinysrgb&w=1920";
const STATEMENT_IMAGE =
    "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const FINAL_IMAGE =
    "https://images.pexels.com/photos/9492092/pexels-photo-9492092.jpeg?auto=compress&cs=tinysrgb&w=1920";

const PROCESS_STEPS = [
    { icon: Tractor, title: "Farm", text: "Native breeds raised slowly on British pasture by families we know by name." },
    { icon: BadgeCheck, title: "Selection", text: "Only exceptional cuts make the grade. Most beef never gets close." },
    { icon: Slice, title: "Butchery", text: "Hand-trimmed by master butchers, sliced with the grain for a perfect bite." },
    { icon: Scale, title: "Preparation", text: "Seasoned with Cornish sea salt. Nothing added, nothing to hide." },
    { icon: Hourglass, title: "Slow Drying", text: "Thirty hours, low and slow, on beechwood racks. Patience is the recipe." },
    { icon: Package, title: "Hand Packing", text: "Each batch checked, packed and numbered by hand. Traceable to the field." },
    { icon: Truck, title: "Delivered", text: "From our drying room to your door, anywhere in Britain." },
];

const DIFFERENT_ITEMS = [
    { icon: Award, label: "100% British Beef" },
    { icon: Layers, label: "Small Batch Crafted" },
    { icon: HeartHandshake, label: "Raised by Trusted Farmers" },
    { icon: Activity, label: "High Protein" },
    { icon: QrCode, label: "Full Traceability" },
    { icon: Gem, label: "Premium Cuts" },
    { icon: Leaf, label: "Minimal Ingredients" },
];

const LIFESTYLE_IMAGES = [
    { src: "/generated/lifestyle-lakes.jpg", label: "The Lakes" },
    { src: "/generated/lifestyle-highlands.jpg", label: "The Highlands" },
    { src: "/generated/lifestyle-road.jpg", label: "The Long Road" },
    { src: "/generated/lifestyle-firstclass.jpg", label: "First Class" },
];

export default function Home() {
    const [products, setProducts] = useState([]);
    const [farms, setFarms] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [selectedFarmId, setSelectedFarmId] = useState(null);
    const [reviewIndex, setReviewIndex] = useState(0);
    const [reducedMotion, setReducedMotion] = useState(false);

    useEffect(() => {
        setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
        Promise.all([getProducts(), getFarms(), getReviews()])
            .then(([p, f, r]) => {
                setProducts(p);
                setFarms(f);
                setReviews(r);
                if (f.length) setSelectedFarmId(f[0].id);
            })
            .catch(() => {});
    }, []);

    const cuts = useMemo(() => products.filter((p) => p.category === "cut" && !p.limited_edition), [products]);
    const gifts = useMemo(() => products.filter((p) => p.category === "gift" || p.category === "bundle"), [products]);
    const selectedFarm = farms.find((f) => f.id === selectedFarmId);
    const visibleReviews = reviews.length ? [0, 1, 2].map((o) => reviews[(reviewIndex + o) % reviews.length]) : [];

    return (
        <main className="bg-[#F6F1E7]">
            {/* ============ 1. HERO ============ */}
            <section className="relative h-screen min-h-[640px] overflow-hidden bg-[#0B0B0C]" data-testid="hero-section">
                <motion.div
                    className="absolute inset-0"
                    animate={reducedMotion ? {} : { scale: [1, 1.09] }}
                    transition={{ duration: 24, ease: "linear", repeat: Infinity, repeatType: "mirror" }}
                >
                    <img src={HERO_IMAGE} alt="British countryside at golden hour" className="w-full h-full object-cover" />
                </motion.div>
                <div className="absolute inset-0 hero-vignette" />
                <div className="absolute inset-0 noise-overlay" />

                <div className="relative z-10 h-full flex flex-col justify-center max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-16">
                    <motion.div
                        initial={{ opacity: 0, y: 34 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="max-w-3xl"
                    >
                        <p className="eyebrow text-[#C9B37A] mb-6 flex items-center gap-2.5">
                            <UnionJack width={16} muted /> Premium British Dried Beef
                        </p>
                        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium text-[#F6F1E7] leading-[1.04] tracking-tight">
                            Raised on British Farms.
                            <br />
                            <span className="italic font-normal">Crafted Without Compromise.</span>
                        </h1>
                        <p className="mt-6 eyebrow text-[#F6F1E7]/60">Dry aged. Slow dried. Nothing else.</p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/shop" className="btn-luxe btn-gold" data-testid="hero-shop-collection-button">
                                Shop Collection
                            </Link>
                            <Link to="/our-story" className="btn-luxe btn-outline-light" data-testid="hero-our-story-button">
                                Our Story
                            </Link>
                        </div>
                    </motion.div>
                </div>

                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2" data-testid="hero-scroll-indicator">
                    <span className="font-mono-meta text-[9px] tracking-[0.3em] text-white/40">SCROLL</span>
                    <div className="w-px h-10 bg-gradient-to-b from-white/50 to-transparent scroll-indicator-dot" />
                </div>
            </section>

            {/* ============ 2. STATEMENT ============ */}
            <section className="relative bg-[#0B0B0C] text-[#F6F1E7]" data-testid="statement-section">
                <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
                    <div className="relative min-h-[380px] lg:min-h-[560px] overflow-hidden">
                        <img src={STATEMENT_IMAGE} alt="Rolling British countryside in morning mist" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0B0B0C]/40" />
                    </div>
                    <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-16 lg:py-24">
                        <Reveal>
                            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight">
                                We don't buy
                                <br />
                                anonymous beef.
                            </h2>
                            <div className="mt-10 space-y-3">
                                <p className="eyebrow text-white/55">We know every farm.</p>
                                <p className="eyebrow text-white/55">We know every field.</p>
                                <p className="eyebrow text-white/55">We know every cut.</p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ============ 3. PROVENANCE MAP ============ */}
            <section className="bg-[#141416] text-[#F6F1E7] py-20 lg:py-28 relative noise-overlay" data-testid="provenance-section">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                    <Reveal className="text-center mb-14">
                        <p className="eyebrow text-[#C9B37A] mb-4">Provenance</p>
                        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium">
                            Know your farmer.
                            <br />
                            <span className="italic font-normal">Know your food.</span>
                        </h2>
                        <p className="mt-5 text-sm text-white/50 max-w-md mx-auto">
                            Every batch can be traced back to where it began. Click a farm on the map to meet the people behind it.
                        </p>
                    </Reveal>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <Reveal>
                            <UKMap farms={farms} selectedId={selectedFarmId} onSelect={setSelectedFarmId} />
                            <div className="flex flex-wrap justify-center gap-2 mt-6" data-testid="provenance-farm-chips">
                                {farms.map((farm) => (
                                    <button
                                        key={farm.id}
                                        onClick={() => setSelectedFarmId(farm.id)}
                                        className={`eyebrow px-4 py-2 border rounded-full transition-colors duration-200 ${
                                            selectedFarmId === farm.id
                                                ? "bg-[#C9B37A] text-[#0B0B0C] border-[#C9B37A]"
                                                : "border-white/20 text-white/60 hover:border-[#C9B37A] hover:text-[#C9B37A]"
                                        }`}
                                        data-testid={`provenance-chip-${farm.id}`}
                                    >
                                        {farm.region}
                                    </button>
                                ))}
                            </div>
                        </Reveal>

                        {selectedFarm && (
                            <Reveal delay={0.15}>
                                <div className="bg-white/[0.04] border border-white/10 rounded-xl overflow-hidden" data-testid="provenance-farm-card">
                                    <div className="h-52 overflow-hidden">
                                        <img src={selectedFarm.images[0]} alt={selectedFarm.name} loading="lazy" className="w-full h-full object-cover" />
                                    </div>
                                    <div className="p-7">
                                        <p className="eyebrow text-[#C9B37A]">{selectedFarm.region}</p>
                                        <h3 className="font-serif-display text-3xl font-medium mt-2" data-testid="provenance-farm-name">
                                            {selectedFarm.name}
                                        </h3>
                                        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5 font-mono-meta text-[10px] tracking-wide text-white/55">
                                            <span>FARMER · {selectedFarm.farmer_name.toUpperCase()}</span>
                                            <span>BREED · {selectedFarm.breed.toUpperCase()}</span>
                                            <span>EST. {selectedFarm.established}</span>
                                            <span>CATTLE · {selectedFarm.cattle_count}</span>
                                            <span className="col-span-2">{selectedFarm.ethos.toUpperCase()}</span>
                                        </div>
                                        <p className="mt-5 text-sm text-white/60 leading-relaxed line-clamp-3">{selectedFarm.story}</p>
                                        <Link
                                            to={`/farms/${selectedFarm.id}`}
                                            className="btn-luxe btn-gold mt-6"
                                            data-testid="provenance-farm-view-button"
                                        >
                                            Meet the Farmer <ArrowRight size={13} />
                                        </Link>
                                    </div>
                                </div>
                            </Reveal>
                        )}
                    </div>
                </div>
            </section>

            {/* ============ 4. MEET THE FARMERS ============ */}
            <section className="bg-[#F6F1E7] py-20 lg:py-28" data-testid="farmers-section">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <Reveal className="order-2 lg:order-1">
                            <div className="grid grid-cols-2 gap-4">
                                {farms.slice(0, 2).map((farm, i) => (
                                    <div key={farm.id} className={`rounded-xl overflow-hidden ${i === 1 ? "mt-10" : ""}`}>
                                        <img src={farm.images[0]} alt={`${farm.farmer_name} of ${farm.name}`} loading="lazy" className="w-full aspect-[3/4] object-cover hover:scale-[1.03] transition-transform duration-700" />
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal className="order-1 lg:order-2" delay={0.1}>
                            <p className="eyebrow text-[#8C6A3D] mb-4">Meet The Farmers</p>
                            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium text-[#0B0B0C] leading-tight">
                                The people behind
                                <br />
                                every pack.
                            </h2>
                            <p className="mt-6 text-base text-black/60 leading-relaxed max-w-md">
                                We don't buy anonymous beef. We work directly with trusted British farming families who raise
                                exceptional cattle with care, patience and pride.
                            </p>
                            <p className="mt-4 text-base text-black/60 leading-relaxed max-w-md">
                                That means every pack has a story worth telling.
                            </p>
                            <Link to="/farms" className="btn-luxe btn-green mt-8" data-testid="farmers-explore-button">
                                Explore Our Farms
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ============ 5. OUR PROCESS ============ */}
            <section className="bg-[#0B0B0C] text-[#F6F1E7] py-20 lg:py-28" data-testid="process-section">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                    <Reveal className="text-center mb-14">
                        <p className="eyebrow text-[#C9B37A] mb-4">The Craft</p>
                        <h2 className="font-serif-display text-4xl sm:text-5xl font-medium">The British Steak Co. Process</h2>
                    </Reveal>
                    <div className="timeline-scroll flex gap-5 overflow-x-auto pb-4" data-testid="process-timeline">
                        {PROCESS_STEPS.map((step, i) => (
                            <motion.div
                                key={step.title}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                                className="timeline-step flex-shrink-0 w-[240px] sm:w-[260px] border border-white/10 rounded-xl p-7 bg-white/[0.03] relative"
                                data-testid={`process-timeline-step-${i}`}
                            >
                                <span className="font-mono-meta text-[10px] text-[#C9B37A]/60">0{i + 1}</span>
                                <step.icon size={30} strokeWidth={1.1} className="text-[#C9B37A] mt-5" />
                                <h3 className="font-serif-display text-2xl font-medium mt-4">{step.title}</h3>
                                <p className="mt-3 text-xs text-white/50 leading-relaxed">{step.text}</p>
                                {i < PROCESS_STEPS.length - 1 && (
                                    <div className="hidden sm:block absolute top-1/2 -right-5 w-5 h-px bg-[#C9B37A]/25" />
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ 6. INGREDIENTS ============ */}
            <section className="bg-[#141416] text-[#F6F1E7] py-20 lg:py-28 border-t border-white/[0.06]" data-testid="ingredients-section">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                    <Reveal className="text-center">
                        <h2 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-medium leading-tight">
                            Two ingredients.
                            <br />
                            <span className="italic font-normal text-[#C9B37A]">Nothing more.</span>
                        </h2>
                        <p className="mt-6 eyebrow text-white/45">Only British Beef. Sea Salt. Nothing Else.*</p>
                    </Reveal>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-14 max-w-4xl mx-auto">
                        <Reveal>
                            <div className="rounded-xl overflow-hidden group">
                                <img src="/generated/ingredient-beef.jpg" alt="Macro texture of premium British fillet" loading="lazy" className="w-full aspect-[16/10] object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                            </div>
                            <p className="font-mono-meta text-[10px] tracking-[0.25em] text-white/45 text-center mt-4">PREMIUM BRITISH BEEF</p>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <div className="rounded-xl overflow-hidden group">
                                <img src="/generated/ingredient-salt.jpg" alt="Macro crystals of Cornish sea salt sparkling on dark slate" loading="lazy" className="w-full aspect-[16/10] object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                            </div>
                            <p className="font-mono-meta text-[10px] tracking-[0.25em] text-white/45 text-center mt-4">CORNISH SEA SALT</p>
                        </Reveal>
                    </div>
                    <p className="font-mono-meta text-[9px] text-white/25 text-center mt-10 tracking-wide">
                        *EVERY PACK. EVERY BATCH. 100% BRITISH BEEF · TRADITIONALLY PROCESSED — NEVER HALAL · NOTHING TO HIDE.
                    </p>
                </div>
            </section>

            {/* ============ 7. PRODUCT COLLECTION ============ */}
            <section className="bg-[#F6F1E7] py-20 lg:py-28" data-testid="collection-section">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                    <Reveal className="text-center mb-14">
                        <p className="eyebrow text-[#8C6A3D] mb-4">The Collection</p>
                        <h2 className="font-serif-display text-4xl sm:text-5xl font-medium text-[#0B0B0C]">Our Cuts</h2>
                        <p className="mt-4 eyebrow text-black/40">Five premium cuts. One simple promise.</p>
                    </Reveal>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                        {cuts.map((product) => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                    <div className="text-center mt-12">
                        <Link to="/shop" className="btn-luxe btn-outline-dark" data-testid="collection-view-all-button">
                            View Full Collection
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============ 8. WHY WE'RE DIFFERENT ============ */}
            <section className="bg-[#0F2A1F] text-[#F6F1E7] py-20 lg:py-24 relative noise-overlay" data-testid="different-section">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                    <Reveal className="text-center mb-14">
                        <h2 className="font-serif-display text-4xl sm:text-5xl font-medium">Why We're Different</h2>
                    </Reveal>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-x-4 gap-y-12">
                        {DIFFERENT_ITEMS.map((item, i) => (
                            <Reveal key={item.label} delay={i * 0.06} className="flex flex-col items-center text-center gap-4">
                                <item.icon size={30} strokeWidth={1.1} className="text-[#C9B37A]" />
                                <span className="eyebrow text-white/70 max-w-[120px]">{item.label}</span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ 9. LIFESTYLE ============ */}
            <section className="bg-[#0B0B0C] py-0" data-testid="lifestyle-section">
                <div className="grid grid-cols-2 lg:grid-cols-4">
                    {LIFESTYLE_IMAGES.map((img, i) => (
                        <div key={i} className="relative group overflow-hidden">
                            <img src={img.src} alt={img.label} loading="lazy" className="w-full aspect-[3/4] object-cover opacity-85 group-hover:opacity-100 group-hover:scale-[1.05] transition-all duration-700" />
                        </div>
                    ))}
                </div>
            </section>

            {/* ============ 10. GIFT COLLECTION ============ */}
            <section className="bg-[#141416] text-[#F6F1E7] py-20 lg:py-28" data-testid="gift-section">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <Reveal className="lg:col-span-4">
                            <Gift size={28} strokeWidth={1.1} className="text-[#C9B37A] mb-6" />
                            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-tight">
                                The Gift
                                <br />
                                Collection
                            </h2>
                            <p className="mt-5 text-sm text-white/55 leading-relaxed max-w-xs">
                                Beautifully crafted magnetic presentation boxes. Perfect for any occasion — unforgettable for every recipient.
                            </p>
                            <Link to="/shop?category=gift" className="btn-luxe btn-gold mt-8" data-testid="gift-shop-button">
                                Shop Gift Boxes
                            </Link>
                        </Reveal>
                        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                            {gifts.map((product, i) => (
                                <Reveal key={product.id} delay={i * 0.1}>
                                    <ProductCard product={product} dark />
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ 10b. THE BEEF CLUB ============ */}
            <section className="bg-[#0F2A1F] text-[#F6F1E7] relative noise-overlay" data-testid="club-section">
                <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
                    <div className="relative min-h-[360px] lg:min-h-[520px] overflow-hidden order-2 lg:order-1">
                        <img src="/generated/club-hero.jpg" alt="A British Beef Club monthly delivery" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0F2A1F]/50" />
                    </div>
                    <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-16 lg:py-24 order-1 lg:order-2">
                        <Reveal>
                            <p className="eyebrow text-[#C9B37A] mb-4">Membership</p>
                            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-tight">
                                The British
                                <br />
                                <span className="italic font-normal">Beef Club.</span>
                            </h2>
                            <p className="mt-5 text-sm text-white/60 leading-relaxed max-w-sm">
                                Britain's finest dried beef, delivered monthly. Member pricing, first access to rare
                                releases, and invitations to the farms themselves.
                            </p>
                            <p className="mt-4 font-mono-meta text-[10px] tracking-[0.2em] text-[#C9B37A]">
                                FROM £33 / MONTH · MEMBER PRICING · CANCEL ANYTIME
                            </p>
                            <Link to="/club" className="btn-luxe btn-gold mt-8 self-start" data-testid="home-club-button">
                                Explore Membership
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ============ 11. REVIEWS ============ */}
            <section className="bg-[#F6F1E7] py-20 lg:py-28" data-testid="reviews-section">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                    <Reveal className="text-center mb-12">
                        <p className="eyebrow text-[#8C6A3D] mb-4">Reviews</p>
                        <h2 className="font-serif-display text-4xl sm:text-5xl font-medium text-[#0B0B0C]">What our customers say</h2>
                    </Reveal>
                    <div className="relative" data-testid="reviews-carousel">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {visibleReviews.map((review, i) => (
                                <div
                                    key={`${review.id}-${i}`}
                                    className={`bg-white/60 border border-black/[0.07] rounded-xl p-8 text-center ${i > 0 ? "hidden md:block" : ""}`}
                                    data-testid="review-card"
                                >
                                    <div className="flex justify-center gap-1 mb-5">
                                        {Array.from({ length: review.rating }).map((_, s) => (
                                            <Star key={s} size={13} className="fill-[#C9B37A] text-[#C9B37A]" />
                                        ))}
                                    </div>
                                    <p className="font-serif-display text-lg italic text-black/75 leading-relaxed">“{review.text}”</p>
                                    <p className="mt-5 font-mono-meta text-[10px] tracking-[0.2em] text-black/45">
                                        {review.name.toUpperCase()} · {review.location.toUpperCase()}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-4 mt-10">
                            <button
                                onClick={() => setReviewIndex((i) => (i - 1 + reviews.length) % reviews.length)}
                                className="w-11 h-11 border border-black/15 rounded-full flex items-center justify-center hover:bg-[#0F2A1F] hover:text-[#F6F1E7] hover:border-[#0F2A1F] transition-colors duration-200"
                                aria-label="Previous reviews"
                                data-testid="reviews-prev-button"
                            >
                                <ChevronLeft size={17} />
                            </button>
                            <button
                                onClick={() => setReviewIndex((i) => (i + 1) % reviews.length)}
                                className="w-11 h-11 border border-black/15 rounded-full flex items-center justify-center hover:bg-[#0F2A1F] hover:text-[#F6F1E7] hover:border-[#0F2A1F] transition-colors duration-200"
                                aria-label="Next reviews"
                                data-testid="reviews-next-button"
                            >
                                <ChevronRight size={17} />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ 12. FINAL CTA ============ */}
            <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden" data-testid="final-cta-section">
                <img src={FINAL_IMAGE} alt="British countryside at sunset" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[#0B0B0C]/55" />
                <Reveal className="relative z-10 text-center px-5 py-24">
                    <h2 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-medium text-[#F6F1E7] leading-tight">
                        From British Fields.
                        <br />
                        <span className="italic font-normal">To Your Hands.</span>
                    </h2>
                    <Link to="/shop" className="btn-luxe btn-gold mt-10 inline-flex" data-testid="final-cta-shop-button">
                        Shop Now
                    </Link>
                </Reveal>
            </section>
        </main>
    );
}
